import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

import { CraditService } from '../../../app/components/credit/shared/services/cradit.service';
import { Section } from '../../../app/components/credit/shared/models/section.model';
import { LeadService } from '../../../app/components/leads/shared/service/leads.service';
import { Setup } from '../../../app/components/setup/shared/service/setup';
import { MODULE_SEQ } from '../../../app/core/shared/models/picklist.model';
import {
  CREDIT_PROPOSAL_ATTACHMENT_DOC_SEQS,
  CREDIT_PROPOSAL_DOCUMENT_DOC_SEQS,
} from '../../../app/components/credit/shared/models/credit-proposal-documents.constants';

/**
 * Where a section's persisted data lives on the backend:
 *  - `blocks`        → GET get-all-sctions, matched by the block's refValSeq
 *  - `financial`     → GET get-fncl-dta/{seq}/{typ} (`crdtFnclDtaLst`)
 *  - `terms`         → GET get-crdt-trm-params/{seq} (`crdtTrmParamsLst`)
 *  - `disbursements` → GET get-prpsd-dsbmnt/{seq} (`prpsdDsbmntRowDTOList`)
 *  - `documents`     → uploaded Credit Proposal documents vs. the required document types
 */
type SavedDataRule =
  /** `blkTyp`, when set, requires a live block of that type (not just any block of the section). */
  | { source: 'blocks'; refValSeq: number; blkTyp?: number }
  | { source: 'financial'; typ: number }
  | { source: 'terms' }
  | { source: 'disbursements' }
  | { source: 'documents' };

/**
 * Explicit per-section rules, keyed by template section id. The block
 * refValSeqs mirror what each section's save writes — not necessarily its
 * template `config.refValSeq` (e.g. the tranche-wise repayment section saves
 * block 2646, while the monthly disbursement/repayment summary saves 2645).
 * A section without a rule is never pre-marked as saved.
 */
const SECTION_SAVED_DATA_RULES: Record<string, SavedDataRule> = {
  'company-introduction': { source: 'blocks', refValSeq: 2519 },
  highlights: { source: 'blocks', refValSeq: 2533 },
  'terms-parameter': { source: 'terms' },
  'Financial-Data': { source: 'financial', typ: 1 },
  'Extra terms Data': { source: 'financial', typ: 3 },
  'income-statement-type-credit-proposal': { source: 'financial', typ: 2 },
  FinancialAnalysisDetails: { source: 'blocks', refValSeq: 2647 },
  'repayment-plan': { source: 'disbursements' },
  'financial-analysis-paragraph': { source: 'blocks', refValSeq: 2521 },
  'irs-rating-summary': { source: 'blocks', refValSeq: 2522 },
  // Risks are blkTyp 2; the conclusion (blkTyp 1) alone does not count.
  'key-risks': { source: 'blocks', refValSeq: 2523, blkTyp: 2 },
  'borrower-request': { source: 'blocks', refValSeq: 2524 },
  'summary-terms': { source: 'blocks', refValSeq: 2682 },
  introduction: { source: 'blocks', refValSeq: 2525 },
  'irs-rating-builder': { source: 'blocks', refValSeq: 2526 },
  'financial-performance': { source: 'blocks', refValSeq: 2527 },
  'microfinance-plus-update': { source: 'blocks', refValSeq: 2529 },
  'borrower-proposal-review': { source: 'blocks', refValSeq: 2530 },
  recommendations: { source: 'blocks', refValSeq: 2532 },
  'proposed-disbursement': { source: 'blocks', refValSeq: 2646 },
  'disbursement-repayment-summary': { source: 'blocks', refValSeq: 2645 },
  documents: { source: 'documents' },
};

/**
 * Derives which Document Builder sections already have data persisted for a
 * Credit Proposal, so save status survives a page reload / reopen.
 *
 * Only the sources needed by the given sections are requested, once each, in
 * parallel. A failed request is treated as "unknown" — its sections are simply
 * not reported, never reported as saved.
 */
@Injectable({
  providedIn: 'root',
})
export class SectionSavedDataService {
  constructor(
    private readonly creditService: CraditService,
    private readonly leadService: LeadService,
    private readonly setup: Setup,
  ) {}

  loadSavedSectionIds(crdtPrpslSeq: number, sections: Section[]): Observable<Set<string>> {
    const rules = sections
      .map((section) => ({ sectionId: section.id, rule: SECTION_SAVED_DATA_RULES[section.id] }))
      .filter((entry): entry is { sectionId: string; rule: SavedDataRule } => !!entry.rule);

    if (crdtPrpslSeq <= 0 || rules.length === 0) {
      return of(new Set<string>());
    }

    const financialTypes = new Set<number>();
    let needsBlocks = false;
    let needsTerms = false;
    let needsDisbursements = false;
    let needsDocuments = false;

    for (const { rule } of rules) {
      switch (rule.source) {
        case 'blocks':
          needsBlocks = true;
          break;
        case 'financial':
          financialTypes.add(rule.typ);
          break;
        case 'terms':
          needsTerms = true;
          break;
        case 'disbursements':
          needsDisbursements = true;
          break;
        case 'documents':
          needsDocuments = true;
          break;
      }
    }

    const financialRequests: Record<string, Observable<boolean | null>> = {};

    financialTypes.forEach((typ) => {
      financialRequests[String(typ)] = this.safely(
        this.creditService.getFinancialData(crdtPrpslSeq, typ),
        (response) => hasLiveRows(unwrapObject(response)?.['crdtFnclDtaLst']),
      );
    });

    return forkJoin({
      blockRefValSeqs: needsBlocks
        ? this.safely(this.creditService.getAllSection(crdtPrpslSeq), liveBlockRefValSeqs)
        : of(null),
      terms: needsTerms
        ? this.safely(this.creditService.getCreditTermParameters(crdtPrpslSeq), (response) =>
            hasLiveRows(unwrapObject(response)?.['crdtTrmParamsLst']),
          )
        : of(null),
      disbursements: needsDisbursements
        ? this.safely(this.creditService.getProposedDisbursements(crdtPrpslSeq), (response) =>
            hasLiveRows(unwrapObject(response)?.['prpsdDsbmntRowDTOList']),
          )
        : of(null),
      documents: needsDocuments ? this.loadDocumentsComplete(crdtPrpslSeq) : of(null),
      financial: Object.keys(financialRequests).length ? forkJoin(financialRequests) : of({}),
    }).pipe(
      map(({ blockRefValSeqs, terms, disbursements, documents, financial }) => {
        const savedSectionIds = new Set<string>();

        for (const { sectionId, rule } of rules) {
          let hasData: boolean | null | undefined;

          switch (rule.source) {
            case 'blocks':
              hasData =
                blockRefValSeqs?.has(
                  rule.blkTyp != null ? `${rule.refValSeq}:${rule.blkTyp}` : String(rule.refValSeq),
                ) ?? null;
              break;
            case 'financial':
              hasData = (financial as Record<string, boolean | null>)[String(rule.typ)];
              break;
            case 'terms':
              hasData = terms;
              break;
            case 'disbursements':
              hasData = disbursements;
              break;
            case 'documents':
              hasData = documents;
              break;
          }

          if (hasData === true) {
            savedSectionIds.add(sectionId);
          }
        }

        return savedSectionIds;
      }),
    );
  }

  /**
   * Whether the Documents tab is complete: every required document type
   * (`isReq` in Document Types setup) of the tab has a live upload. When no
   * type is marked required, any upload counts. `null` when it can't be told.
   * Uploads save themselves, so there is no separate Save for this tab.
   */
  loadDocumentsComplete(crdtPrpslSeq: number): Observable<boolean | null> {
    const tabDocSeqs = new Set<number>([
      ...CREDIT_PROPOSAL_DOCUMENT_DOC_SEQS,
      ...CREDIT_PROPOSAL_ATTACHMENT_DOC_SEQS,
    ]);

    return forkJoin({
      types: this.setup.getAllDocumentByCategry(MODULE_SEQ.CREDIT_PROPOSAL),
      uploaded: this.leadService.getLeadDocs(crdtPrpslSeq, MODULE_SEQ.CREDIT_PROPOSAL),
    }).pipe(
      map(({ types, uploaded }) => {
        const uploadedTypes = new Set(
          unwrapArray(uploaded)
            .filter(isLive)
            .map((doc) => Number((doc as { docTyp?: unknown }).docTyp)),
        );

        const requiredTypes = unwrapArray(types)
          .filter((type) => Boolean((type as { isReq?: unknown }).isReq))
          .map((type) => Number((type as { docSeq?: unknown }).docSeq))
          .filter((docSeq) => tabDocSeqs.has(docSeq));

        return requiredTypes.length > 0
          ? requiredTypes.every((docSeq) => uploadedTypes.has(docSeq))
          : Array.from(tabDocSeqs).some((docSeq) => uploadedTypes.has(docSeq));
      }),
      catchError((error) => {
        console.error('Unable to load Credit Proposal documents status:', error);
        return of(null);
      }),
    );
  }

  /** Maps a response, turning both HTTP errors and unparseable bodies into `null` ("unknown"). */
  private safely<T>(request: Observable<unknown>, project: (response: unknown) => T): Observable<T | null> {
    return request.pipe(
      map((response) => project(response)),
      catchError((error) => {
        console.error('Unable to load saved section data:', error);
        return of(null);
      }),
    );
  }
}

/**
 * Normalizes the `{ data: ... }` wrapper some responses use. An empty 200 body
 * (how get-fncl-dta / get-crdt-trm-params answer for a proposal with nothing
 * saved yet) comes through as `null`.
 */
function unwrapObject(response: unknown): Record<string, unknown> | null {
  const payload = (response as { data?: unknown } | null)?.data ?? response;
  const record = Array.isArray(payload) ? payload[0] : payload;

  return record && typeof record === 'object' ? (record as Record<string, unknown>) : null;
}

function unwrapArray(response: unknown): unknown[] {
  const payload = (response as { data?: unknown } | null)?.data ?? response;
  return Array.isArray(payload) ? payload : [];
}

function isLive(row: unknown): boolean {
  return !!row && typeof row === 'object' && Number((row as { delFlg?: unknown }).delFlg ?? 0) !== 1;
}

function hasLiveRows(rows: unknown): boolean {
  return Array.isArray(rows) && rows.some(isLive);
}

/** Keys of the live blocks: `"<refValSeq>"` and `"<refValSeq>:<blkTyp>"`. */
function liveBlockRefValSeqs(response: unknown): Set<string> {
  const keys = new Set<string>();

  for (const block of unwrapArray(response)) {
    if (isLive(block)) {
      const { refValSeq, blkTyp } = block as { refValSeq?: unknown; blkTyp?: unknown };

      keys.add(String(Number(refValSeq)));
      keys.add(`${Number(refValSeq)}:${Number(blkTyp)}`);
    }
  }

  return keys;
}
